import { call, put, all, takeLatest, select } from 'redux-saga/effects';
import { IChangePayload, ISubscriptionPlan, types } from './types';
import {
    setSubscription,
    setSubscriptionSuccess,
    setSubscriptionFail,
    getSubscriptionPlansSuccess,
    getSubscriptionPlansFail,
    cancelSubsciptionSuccess,
    cancelSubsciptionFail,
    updateSubscriptionSuccess,
    updateSubscriptionFail,
    activateCreatorSubscriptionSuccess,
    activateCreatorSubscriptionFail,
    subscribeSuccess,
    subscribeFail,
    fetchAdminCreatorGrantPlans,
    fetchAdminCreatorGrantPlansSuccess,
    fetchAdminCreatorGrantPlansFail,
    grantCreatorSubscriptionAdmin,
    grantCreatorSubscriptionAdminSuccess,
    grantCreatorSubscriptionAdminFail,
} from './actions';
import { globalErrorHandler } from '../error/saga';
import { api } from '../../configurations/api';
import {
    getSubscriptionPlansUrl,
    getSubscriptionPlansPaystackUrl,
    getSubscriptionPlansFlutterwaveUrl,
    getMySubscriptionUrl,
    cancelSubscriptionUrl,
    updateSubscriptionUrl,
    activateCreatorSubscriptionUrl,
    createSubscriptionUrl,
    createSubscriptionStripeUrl,
    initializePaystackSubscriptionUrl,
    verifyPaystackSubscriptionUrl,
    initializeFlutterwaveSubscriptionUrl,
    verifyFlutterwaveSubscriptionUrl,
    adminCreatorSubscriptionPlansUrl,
    adminCreatorSubscriptionGrantUrl,
    paymentRailUrl,
} from '../../configurations/api/url';
import { httpRequest } from '../types';
import { SentryCapture } from '../../analytics/Sentry';
import { showToastMessage } from '../../utils/AppUtils';
import {
    parseGetMyPlanPrimarySubscription,
    parseUpdateCreatorSubscriptionPaymentResult,
} from '../../utils/creatorSubscriptionApi';
import { normalizeCreatorGrantablePlans } from '../../utils/adminCreatorSubscriptionGrant';
import { requireApiPayload } from '../../utils/campaignHostSubscriptionApi';
import type {
    CreatorGrantPaymentSource,
    IGrantCreatorSubscriptionPayload,
    IGrantCreatorSubscriptionResult,
} from '../../types/global/creatorSubscription.types';
import { effectiveOperatingCountryCodeSelector } from '../../selectors/selectors';
import {
    formatCreatorTrialStartedToast,
    resolveCreatorTrialDays,
    isCreatorTrialAvailable,
    pickCreatorTrial,
} from '../../utils/creatorSubscriptionTrial';
import { resolvePaymentRail, defaultCheckoutProvider } from '../../utils/paymentRail';
import { plansSelector, userSubscriptionSelector } from '../../pages/EsSettings/selector';

/** Never forward start_trial once eligibility says the lifetime trial is gone. */
function* resolveRequestedStartTrial(requested?: boolean): Generator<any, boolean, any> {
    if (!requested) return false;
    const subscription = yield select(userSubscriptionSelector);
    const plans = yield select(plansSelector);
    const trial = pickCreatorTrial(subscription, plans);
    // Catalog / get-my-plan not loaded yet — trust the UI gate.
    if (!trial) return true;
    return isCreatorTrialAvailable(trial);
}

function* resolveCreatorPlanCatalogUrl(): Generator<any, string, any> {
    try {
        const response = yield call(api, `${paymentRailUrl}?category=subscriptions`, httpRequest.GET, null);
        const envelope = response?.data ?? response;
        const data = envelope?.data ?? envelope;
        const rail = resolvePaymentRail(data);
        const provider = defaultCheckoutProvider(rail);
        if (provider === 'paystack') return getSubscriptionPlansPaystackUrl;
        if (provider === 'flutterwave') return getSubscriptionPlansFlutterwaveUrl;
        return getSubscriptionPlansUrl;
    } catch {
        const countryCode: string | undefined = yield select(effectiveOperatingCountryCodeSelector);
        const isNigeria = (countryCode ?? '').toUpperCase() === 'NG';
        return isNigeria ? getSubscriptionPlansPaystackUrl : getSubscriptionPlansUrl;
    }
}

function* getSubscriptionPlan(): any {
    try {
        const url = yield call(resolveCreatorPlanCatalogUrl);
        const response = yield call(api, url, httpRequest.GET, null, 3, 0);
        const body = response?.data ?? response;
        const raw = body?.data;
        const plans = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : Array.isArray(body) ? body : [];
        const trial = body?.trial;
        const plansWithTrial =
            trial && typeof trial === 'object' ? plans.map((p: any) => (p?.trial ? p : { ...p, trial })) : plans;
        yield put(getSubscriptionPlansSuccess(plansWithTrial));
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(getSubscriptionPlansFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* setSubscriptionWorker({ resolve, reject }: any): any {
    try {
        const response = yield call(api, getMySubscriptionUrl, httpRequest.GET, null, 5, 0);
        // `api()` already unwraps axios `response.data` (full get-my-plan body).
        const subscription = parseGetMyPlanPrimarySubscription(response);
        yield put(setSubscriptionSuccess(subscription));
        if (resolve) resolve(subscription);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(setSubscriptionFail(error));
        yield call(globalErrorHandler, error);
        if (reject) reject(error);
    }
}

function* cancelSubscription({ payload, resolve, reject }: any): any {
    try {
        const request = `${cancelSubscriptionUrl}${payload}`;
        const response = yield call(api, request, httpRequest.PATCH, {}, 3, 0);
        const { data } = response;
        yield put(cancelSubsciptionSuccess(data));
        yield put(setSubscription());
        resolve(data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        const { data } = error || {};
        yield put(cancelSubsciptionFail(error));
        yield call(globalErrorHandler, error.message);
        reject(data);
    }
}

function* fetchAndStoreCreatorSubscription(): Generator<any, ISubscriptionPlan | undefined, any> {
    const response = yield call(api, getMySubscriptionUrl, httpRequest.GET, null, 5, 0);
    const subscription = parseGetMyPlanPrimarySubscription(response);
    yield put(setSubscriptionSuccess(subscription));
    return subscription;
}

function* subscribe({ payload, resolve, reject }: { payload: IChangePayload; resolve: any; reject: any }): any {
    try {
        if (payload.isPaystack) {
            yield call(
                api,
                createSubscriptionUrl,
                httpRequest.POST,
                { plan: payload.plan, authorization: payload.authorization },
                2,
                3000,
            );
            const subscription = yield call(fetchAndStoreCreatorSubscription);
            if (subscription) {
                yield put(subscribeSuccess(subscription));
            }
            yield call(showToastMessage, 'Subscription Success', 'success');
            if (resolve) resolve(subscription);
        } else {
            const startTrial = yield* resolveRequestedStartTrial(payload.start_trial);
            const body = {
                priceId: payload.priceId,
                paymentMethodId: payload.paymentMethodId,
                isUpgrade: payload.isUpgrade,
                ...(startTrial ? { start_trial: true } : {}),
            };
            yield call(api, createSubscriptionStripeUrl, httpRequest.POST, body, 2, 3000);
            const subscription = yield call(fetchAndStoreCreatorSubscription);
            if (subscription) {
                yield put(subscribeSuccess(subscription));
            }
            const trialDays = resolveCreatorTrialDays(
                subscription?.trial ?? subscription?.subscription_details ?? (startTrial ? { days: undefined } : null),
            );
            yield call(
                showToastMessage,
                startTrial
                    ? formatCreatorTrialStartedToast(subscription?.subscription_details?.trial_days ?? trialDays)
                    : 'Subscription Success',
                'success',
            );
            if (resolve) resolve(subscription);
        }
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(subscribeFail(error));
        yield call(globalErrorHandler, error);
        if (reject) reject(error);
    }
}

function* updateSubscription({ payload, resolve, reject }: any): any {
    try {
        const {
            subscriptionId,
            paymentMethodId,
            payment_method_id: paymentMethodIdSnake,
            callback_url,
        } = payload ?? {};
        const pmId = paymentMethodId || paymentMethodIdSnake;
        const request = `${updateSubscriptionUrl}${subscriptionId}`;
        const body = {
            ...(pmId ? { paymentMethodId: pmId, payment_method_id: pmId } : {}),
            ...(callback_url ? { callback_url } : {}),
        };
        const response = yield call(api, request, httpRequest.PATCH, body, 2, 3000);
        const result =
            parseUpdateCreatorSubscriptionPaymentResult(response.data) ?? response.data?.data ?? response.data;
        yield put(updateSubscriptionSuccess(result));
        if (result?.action === 'updated') {
            yield put(setSubscription());
        }
        resolve(result);
    } catch (error: any) {
        SentryCapture(error, 'error');
        const { data } = error || {};
        yield put(updateSubscriptionFail(error));
        yield call(globalErrorHandler, data?.message ?? error.message);
        reject(data ?? error);
    }
}

function* activateCreatorSubscriptionWorker({ payload, resolve, reject }: any): any {
    try {
        const request = `${activateCreatorSubscriptionUrl}${payload}`;
        const response = yield call(api, request, httpRequest.PATCH, {}, 3, 0);
        const { data } = response;
        yield put(activateCreatorSubscriptionSuccess(data));
        yield put(setSubscription());
        resolve(data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        const body = error?.data ?? error;
        yield put(activateCreatorSubscriptionFail(error));
        // Client handles resubscribe checkout for this code — avoid duplicate toasts.
        if (body?.code !== 'reactivation_unavailable') {
            yield call(globalErrorHandler, error);
        }
        reject(body ?? error);
    }
}

function* getSubscriptionPlanWatcher() {
    yield takeLatest(types.GET_SUBSCRIPTION_PLANS, getSubscriptionPlan);
}

function* getMySubscriptionWatcher() {
    yield takeLatest(types.SET_SUBSCRIPTION, setSubscriptionWorker);
}

function* cancelSubscriptionWatcher() {
    yield takeLatest(types.CANCEL_SUBSCRIPTION, cancelSubscription);
}

function* subscribeWatcher() {
    yield takeLatest<any>(types.SUBSCRIBE, subscribe);
}

function* initializePaystackSubscriptionWorker({ payload, resolve, reject }: any): any {
    try {
        const startTrial = yield* resolveRequestedStartTrial(payload?.start_trial);
        const body = {
            ...payload,
            ...(startTrial ? { start_trial: true } : {}),
        };
        if (!startTrial) delete body.start_trial;
        const response = yield call(api, initializePaystackSubscriptionUrl, httpRequest.POST, body, 2, 3000);
        const { data } = response;
        if (resolve) resolve(data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield call(globalErrorHandler, error);
        if (reject) reject(error);
    }
}

function* verifyPaystackSubscriptionWorker({ payload, resolve, reject }: any): any {
    try {
        const response = yield call(api, verifyPaystackSubscriptionUrl, httpRequest.POST, payload, 2, 3000);
        const data = response?.data ?? response;
        if (data && data.verified === false) {
            const err: any = new Error(
                data.paystack_status
                    ? `Payment not complete yet (${data.paystack_status}). Please wait a moment and try again.`
                    : 'Payment could not be verified.',
            );
            err.data = data;
            if (reject) reject(err);
            return;
        }
        const subscription = yield call(fetchAndStoreCreatorSubscription);
        if (resolve) resolve(subscription ?? data?.subscription);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield call(globalErrorHandler, error);
        if (reject) reject(error);
    }
}

function* initializeFlutterwaveSubscriptionWorker({ payload, resolve, reject }: any): any {
    try {
        const startTrial = yield* resolveRequestedStartTrial(payload?.start_trial);
        const body = {
            ...payload,
            ...(startTrial ? { start_trial: true } : {}),
        };
        if (!startTrial) delete body.start_trial;
        const response = yield call(api, initializeFlutterwaveSubscriptionUrl, httpRequest.POST, body, 2, 3000);
        const { data } = response;
        if (resolve) resolve(data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield call(globalErrorHandler, error);
        if (reject) reject(error);
    }
}

function* verifyFlutterwaveSubscriptionWorker({ payload, resolve, reject }: any): any {
    try {
        const body: Record<string, string> = {};
        if (payload.transaction_id) body.transaction_id = payload.transaction_id;
        if (payload.tx_ref) body.tx_ref = payload.tx_ref;
        if (payload.reference) body.reference = payload.reference;
        const response = yield call(api, verifyFlutterwaveSubscriptionUrl, httpRequest.POST, body, 2, 3000);
        const data = response?.data ?? response;
        if (data && data.verified === false) {
            const err: any = new Error(
                data.flutterwave_status
                    ? `Payment not complete yet (${data.flutterwave_status}). Please wait a moment and try again.`
                    : 'Payment could not be verified.',
            );
            err.data = data;
            if (reject) reject(err);
            return;
        }
        const subscription = yield call(fetchAndStoreCreatorSubscription);
        if (resolve) {
            resolve({
                ...(subscription ?? data?.subscription ?? {}),
                updated_card: Boolean(data?.updated_card),
            });
        }
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield call(globalErrorHandler, error);
        if (reject) reject(error);
    }
}

function* updateSubscriptionWatcher() {
    yield takeLatest(types.UPDATE_SUBSCRIPTION, updateSubscription);
}

function* initializePaystackSubscriptionWatcher() {
    yield takeLatest<any>(types.INITIALIZE_PAYSTACK_SUBSCRIPTION, initializePaystackSubscriptionWorker);
}

function* verifyPaystackSubscriptionWatcher() {
    yield takeLatest<any>(types.VERIFY_PAYSTACK_SUBSCRIPTION, verifyPaystackSubscriptionWorker);
}

function* initializeFlutterwaveSubscriptionWatcher() {
    yield takeLatest<any>(types.INITIALIZE_FLUTTERWAVE_SUBSCRIPTION, initializeFlutterwaveSubscriptionWorker);
}

function* verifyFlutterwaveSubscriptionWatcher() {
    yield takeLatest<any>(types.VERIFY_FLUTTERWAVE_SUBSCRIPTION, verifyFlutterwaveSubscriptionWorker);
}

function* activateCreatorSubscriptionWatcher() {
    yield takeLatest(types.ACTIVATE_CREATOR_SUBSCRIPTION, activateCreatorSubscriptionWorker);
}

function* fetchAdminCreatorGrantPlansWorker({
    payload,
}: {
    payload: { source: CreatorGrantPaymentSource; region?: string };
}): Generator<any, void, any> {
    try {
        const source = payload?.source;
        if (!source) throw new Error('source is required (stripe or paystack)');
        const params = new URLSearchParams({ source });
        // Mirror GET /plan/stripe: non-Africa settings use region=Global ($19.99…),
        // not Africa Stripe rows ($1.99…).
        const region = payload?.region || (source === 'stripe' ? 'Global' : undefined);
        if (region) params.set('region', region);
        const url = `${adminCreatorSubscriptionPlansUrl}?${params.toString()}`;
        const response = yield call(api, url, httpRequest.GET, null, 2);
        const data = requireApiPayload(response);
        const plans = normalizeCreatorGrantablePlans(data);
        yield put(fetchAdminCreatorGrantPlansSuccess(plans));
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(fetchAdminCreatorGrantPlansFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* grantCreatorSubscriptionAdminWorker({
    payload,
}: {
    payload: IGrantCreatorSubscriptionPayload;
}): Generator<any, void, any> {
    try {
        const response = yield call(api, adminCreatorSubscriptionGrantUrl, httpRequest.POST, payload, 2);
        const data = requireApiPayload<IGrantCreatorSubscriptionResult>(response);
        yield put(grantCreatorSubscriptionAdminSuccess(data));
        yield call(showToastMessage, 'Creator subscription granted', 'success');
    } catch (error: any) {
        SentryCapture(error, 'error');
        const message = error?.data?.message ?? error?.message ?? 'Unable to grant creator subscription';
        yield call(showToastMessage, message, 'error');
        yield put(grantCreatorSubscriptionAdminFail(error));
    }
}

function* fetchAdminCreatorGrantPlansWatcher() {
    yield takeLatest(fetchAdminCreatorGrantPlans, fetchAdminCreatorGrantPlansWorker);
}

function* grantCreatorSubscriptionAdminWatcher() {
    yield takeLatest(grantCreatorSubscriptionAdmin, grantCreatorSubscriptionAdminWorker);
}

export default function* subscriptionSaga() {
    yield all([
        getSubscriptionPlanWatcher(),
        getMySubscriptionWatcher(),
        cancelSubscriptionWatcher(),
        subscribeWatcher(),
        updateSubscriptionWatcher(),
        initializePaystackSubscriptionWatcher(),
        verifyPaystackSubscriptionWatcher(),
        initializeFlutterwaveSubscriptionWatcher(),
        verifyFlutterwaveSubscriptionWatcher(),
        activateCreatorSubscriptionWatcher(),
        fetchAdminCreatorGrantPlansWatcher(),
        grantCreatorSubscriptionAdminWatcher(),
    ]);
}
